// Pure helper for diffing two CustomExercise revisions for the "Update Available"
// dialog (issue #3198 Phase 1). Lives outside exercise-db.service.ts so the
// test runner can import it without pulling in Angular DI.

import { CustomExercise, EXERCISE_DIFF_FIELDS, ExerciseDiffField } from '@cf-shared/data-model/exercise';
import { deepClone, deepCompareEQ } from '@cf-shared/deep-helpers';

// the field list lives in shared (so `coachBaseFields` can derive its type from it);
// re-exported here so callers keep importing it from the diff module
export { EXERCISE_DIFF_FIELDS };
export type { ExerciseDiffField };

export interface ExerciseDiffEntry {
  field: ExerciseDiffField;
  oldValue: unknown;
  newValue: unknown;
  oldDisplay: string;
  newDisplay: string;
}

// Length above which a text field's before/after is shown via full-page links (each opens
// the whole value on its own page) instead of inline, keeping the diff list compact.
export const DESCRIPTION_INLINE_MAX = 120;

// Raw before/after text of a diff entry, normalising null/empty to ''. (`oldDisplay` /
// `newDisplay` collapse long text to a label like "Description changed"; these carry the raw.)
export function diffValueText(value: unknown): string {
  return value == null || value === '' ? '' : String(value);
}

// True when either side of the entry exceeds the inline cap — caller shows full-page links
// instead of dumping the text into a row.
export function isLongTextDiff(entry: ExerciseDiffEntry, max: number = DESCRIPTION_INLINE_MAX): boolean {
  return diffValueText(entry.oldValue).length > max || diffValueText(entry.newValue).length > max;
}

export interface ExerciseDiffResolvers {
  resolveEquipmentName: (id: string | undefined) => string;
  resolveExerciseName: (id: string | undefined) => string;
  // localized "Toggled on" / "Toggled off" / "Description changed" / "Strength standard changed" labels
  toggledOn: string;
  toggledOff: string;
  descriptionChanged: string;
  standardChanged: string;
  empty: string;
}

// how one side of a changed field is shown in the picker
enum DiffDisplay {
  // the raw value, or the localized "(empty)" placeholder for null/empty
  Plain,
  // a single "Description changed" line — the markdown body would blow out the row
  DescriptionChanged,
  // a single "Strength standard changed" line — it is a nested structure
  StandardChanged,
  // "Toggled on" / "Toggled off"
  Toggle,
  // the id resolved to a name by the caller-supplied resolver
  EquipmentName,
  ExerciseName,
}

interface DiffFieldSpec {
  // true when the two values count as the same (after the field's own normalisation)
  isSame: (a: unknown, b: unknown) => boolean;
  display: DiffDisplay;
  // copy by deep clone (non-primitive) so a copied value never aliases the source doc
  deepCloneOnCopy: boolean;
}

// treat undefined, null and '' as the same empty string
const sameText = (a: unknown, b: unknown) => (a || '') === (b || '');
// treat null/undefined as 0 so a never-set field doesn't diff against an explicit 0
const sameNumberOrZero = (a: unknown, b: unknown) => (a ?? 0) === (b ?? 0);
// coerce via !! so undefined and false are the same state
const sameFlag = (a: unknown, b: unknown) => !!a === !!b;

// One row per diffable field. Typed as a Record over `ExerciseDiffField`, so a field added to
// `EXERCISE_DIFF_FIELDS` without a row here is a compile error rather than a field that
// silently never shows up in a diff.
const DIFF_FIELD_SPECS: Record<ExerciseDiffField, DiffFieldSpec> = {
  name:                { isSame: sameText,         display: DiffDisplay.Plain,              deepCloneOnCopy: false },
  shortname:           { isSame: sameText,         display: DiffDisplay.Plain,              deepCloneOnCopy: false },
  description:         { isSame: sameText,         display: DiffDisplay.DescriptionChanged, deepCloneOnCopy: false },
  videoUrl:            { isSame: sameText,         display: DiffDisplay.Plain,              deepCloneOnCopy: false },
  bw_pc:               { isSame: sameNumberOrZero, display: DiffDisplay.Plain,              deepCloneOnCopy: false },
  allowMyoReps:        { isSame: sameFlag,         display: DiffDisplay.Toggle,             deepCloneOnCopy: false },
  avoidRMTesting:      { isSame: sameFlag,         display: DiffDisplay.Toggle,             deepCloneOnCopy: false },
  standard:            { isSame: deepCompareEQ,    display: DiffDisplay.StandardChanged,    deepCloneOnCopy: true },
  equipmentTemplateId: { isSame: sameText,         display: DiffDisplay.EquipmentName,      deepCloneOnCopy: false },
  similarExerciseId:   { isSame: sameText,         display: DiffDisplay.ExerciseName,       deepCloneOnCopy: false },
};

// turn one side of a changed field into the text the picker row shows
function displayDiffValue(display: DiffDisplay, value: unknown, resolvers: ExerciseDiffResolvers): string {
  switch (display) {
    case DiffDisplay.Plain: return value == null || value === '' ? resolvers.empty : String(value);
    case DiffDisplay.DescriptionChanged: return resolvers.descriptionChanged;
    case DiffDisplay.StandardChanged: return resolvers.standardChanged;
    case DiffDisplay.Toggle: return value ? resolvers.toggledOn : resolvers.toggledOff;
    case DiffDisplay.EquipmentName: return resolvers.resolveEquipmentName(value as string | undefined);
    case DiffDisplay.ExerciseName: return resolvers.resolveExerciseName(value as string | undefined);
    default: { const _: never = display; return _; }
  }
}

// Copy one diffable field from `source` onto `target`, deep-cloning where the field's row says
// so. Shared by the base snapshot and the selective applier so both copy a field the same way.
export function copyDiffField<K extends ExerciseDiffField>(
  target: Partial<Pick<CustomExercise, ExerciseDiffField>>,
  source: CustomExercise,
  field: K,
): void {
  const value = source[field];
  target[field] = DIFF_FIELD_SPECS[field].deepCloneOnCopy ? deepClone(value) : value;
}

export function buildExerciseDiff(
  local: CustomExercise,
  remote: CustomExercise,
  resolvers: ExerciseDiffResolvers,
): ExerciseDiffEntry[] {
  const out: ExerciseDiffEntry[] = [];
  // for each field in picker order, if the two sides differ, add an entry
  for (const field of EXERCISE_DIFF_FIELDS) {
    const spec = DIFF_FIELD_SPECS[field];
    const oldValue = local[field];
    const newValue = remote[field];
    if (spec.isSame(oldValue, newValue)) continue;
    out.push({
      field, oldValue, newValue,
      oldDisplay: displayDiffValue(spec.display, oldValue, resolvers),
      newDisplay: displayDiffValue(spec.display, newValue, resolvers),
    });
  }
  return out;
}

// 3-way update diff for fork updates (issue #3198 Phase 3). Offers a field in the update
// picker ONLY when the coach changed it since the fork's base — i.e. it appears in BOTH:
//   - `buildExerciseDiff(fork, source)`  → fork's current value differs from source-now
//   - `buildExerciseDiff(base, source)`  → coach changed it since the review base
// Entries are the fork → source-now form (so the user sees what they'd replace), filtered
// to the coach-changed set. This suppresses the user's own fork-time divergences (e.g. a
// rename) while still surfacing a field the coach changed that the user ALSO diverged (a
// real conflict). When `base` is undefined (pre-existing forks with no snapshot yet) it
// falls back to the plain 2-way `buildExerciseDiff(fork, source)`.
export function buildCoachUpdateDiff(
  fork: CustomExercise,
  source: CustomExercise,
  base: CustomExercise | undefined,
  resolvers: ExerciseDiffResolvers,
): ExerciseDiffEntry[] {
  const forkVsSource = buildExerciseDiff(fork, source, resolvers);
  if (!base) return forkVsSource;
  const coachChangedFields = new Set(buildExerciseDiff(base, source, resolvers).map(e => e.field));
  return forkVsSource.filter(e => coachChangedFields.has(e.field));
}
